import { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { Context as HelperContext } from '../../providers/HelperContextProvider';
import { Context as RegistrationContext } from '../../providers/RegistrationProvider';
import { Context as AuthContext } from '../../providers/AuthProvider';
import { useCartStorageController } from './useCartStorageController';
import { createFetchStoreCart } from './webstoreCheckoutUtilities';
import { useCartController } from './useCartController';
import { useCartShippingController } from './useCartShippingController';

const useRegCartController = ({
  fetchShippingOnAddressChange = false
} = {}) => {
  const { search } = useLocation();
  const { state: authState } = useContext(AuthContext);
  const {
    state: helperState,
    toggleWebstoreCheckoutSummaryDrawer
  } = useContext(HelperContext);
  const {
    state,
    fetchRegCart,
    fetchRegCartCheckoutSessionResult,
    fetchCheckoutInvoiceResult,
    addProductsToRegCart,
    updateProductsInRegCart,
    removeProductsFromRegCart,
    redeemRegCartProductCoupon,
    checkoutRegCart
  } = useContext(RegistrationContext);
  const {
    loading: checkoutSessionLoading,
    value: checkoutSession
  } = state.regCartCheckoutSession;
  const { loading, value: userCart } = state.regCart;
  const isCartFetching = loading || !authState.tokenAttempted;

  const cartStorageController = useCartStorageController();
  const { getGuestCartStorage } = cartStorageController;

  const cartShippingController = useCartShippingController(
    authState.userData?.user.pkUser,
    {
      isAuthenticated: !!authState.userData?.user,
      fetchShippingOnAddressChange,
      shippingCostMissing: userCart?.cart.shippingCostMissing,
      fetchUserCart: (pkUser, data, onSuccessCallback, onErrorCallback) =>
        fetchRegCart(data, onSuccessCallback, onErrorCallback)
    }
  );
  const { onShippingCallback } = cartShippingController;

  const fetchUserCart = (pkUser, data, onSuccessCallback, onErrorCallback) => {
    fetchRegCart(data, onSuccessCallback, onErrorCallback);
  };

  const onFetchUserCart = createFetchStoreCart({
    isAuthenticated: !!authState.userData?.user,
    getGuestCartStorage,
    onShippingCallback,
    fetchUserCart
  });

  const onAddProductsToRegCart = (
    pkUser,
    data,
    onSuccessCallback,
    onErrorCallback
  ) => {
    addProductsToRegCart(data, onSuccessCallback, onErrorCallback);
  };

  const onUpdateRegCartProducts = (
    pkUser,
    data,
    onSuccessCallback,
    onErrorCallback
  ) => {
    updateProductsInRegCart(data, onSuccessCallback, onErrorCallback);
  };

  const onRemoveRegCartProducts = (
    pkUser,
    data,
    onSuccessCallback,
    onErrorCallback
  ) => {
    removeProductsFromRegCart(
      {
        ...data,
        pkRegCartEcomStoreProductVariants: data.pkEcomStoreCartProducts,
        pkRegCartEcomStoreProductPackages: data.pkEcomStoreCartPackages
      },
      onSuccessCallback,
      onErrorCallback
    );
  };

  const onRedeemRegCartProductCoupon = (
    pkUser,
    data,
    onSuccessCallback,
    onErrorCallback
  ) => {
    redeemRegCartProductCoupon(data, onSuccessCallback, onErrorCallback);
  };

  const onCheckoutUserCart = (
    pkUser,
    data,
    onSuccessCallback,
    onErrorCallback
  ) => {
    checkoutRegCart(data, onSuccessCallback, onErrorCallback);
  };

  const onFetchCheckoutSessionResult = (
    pkUser,
    data,
    onSuccessCallback,
    onErrorCallback
  ) => {
    fetchRegCartCheckoutSessionResult(data, onSuccessCallback, onErrorCallback);
  };

  const onFetchCheckoutInvoiceResult = (
    pkUser,
    data,
    onSuccessCallback,
    onErrorCallback
  ) => {
    fetchCheckoutInvoiceResult(data, onSuccessCallback, onErrorCallback);
  };

  const onShowProductCustomization = ({
    ecomStoreCartProduct,
    ecomStoreCartPackage
  }) => {
    toggleWebstoreCheckoutSummaryDrawer({
      ...helperState.webstore.checkoutSummaryDrawer,
      showProductCustomization: true,
      ecomStoreCartProduct,
      ecomStoreCartPackage
    });
  };

  const getCheckoutPaymentParamStatus = () => {
    const params = new URLSearchParams(search.toLowerCase());
    const urlCode = params.get('code');
    const paymentSessionId = params.get('sessionid');
    const paymentSecureId = params.get('secureid');
    const paymentStatus = params.get('status');
    const showPaymentComplete =
      (paymentStatus === 'success' || paymentStatus === '5000') &&
      (!!paymentSecureId || !!paymentSessionId);

    return {
      urlCode,
      paymentSessionId,
      paymentSecureId,
      paymentStatus,
      showPaymentComplete
    };
  };

  return {
    isCartFetching,
    userCart,
    ecomStoreCart: userCart?.ecomStoreCart,
    checkoutSession,
    checkoutSessionLoading,
    checkoutPaymentParamStatus: getCheckoutPaymentParamStatus(),
    getStorageCart: () => getGuestCartStorage(authState.userData?.user.pkUser),
    ...useCartController(authState.userData?.user.pkUser, {
      user: authState.userData?.user,
      userCart,
      fetchShippingOnAddressChange,
      onFetchUserCart,
      onFetchCheckoutSessionResult,
      onFetchCheckoutInvoiceResult,
      onCheckoutUserCart,
      onAddProductsToCart: onAddProductsToRegCart,
      onUpdateCartProducts: onUpdateRegCartProducts,
      onRemoveProductsFromCart: onRemoveRegCartProducts,
      onRedeemCartProductCoupon: onRedeemRegCartProductCoupon,
      onShowProductCustomization,
      cartShippingController,
      cartStorageController
    })
  };
};

export { useRegCartController };
